import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { FirmSelectors } from '@app/core/state/firm/firm.selectors';
import { openTranslatableDialog } from '@app/shared/misc/openDialog';
import { MenuItemProps } from '@bl/components';
import { Client } from '@core/entity/client';
import { AmlService } from '@core/services/aml.service';
import { AmlEntityType, AmlStatus } from '@core/state/types/aml.types';
import { TooltipOptions } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { BehaviorSubject, Observable, combineLatest, filter, map, mergeMap, take, tap } from 'rxjs';
import { getTranslationProvider } from 'src/app/core/i18n/getTranslationProvider';
import { TranslatableComponent } from 'src/app/core/i18n/TranslatableComponent';
import { CreateManualAmlDialogComponent } from './create-manual-aml.dialog.component';
import { InputExistingAmlIdDialogComponent } from './input-existing-aml-id.dialog.component';

@Component({
  selector: 'app-client-aml',
  templateUrl: './client-aml.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [getTranslationProvider('ClientAmlComponent')],
})
export class ClientAmlComponent extends TranslatableComponent {
  @Input() client: Client;
  @Input()
  set active(value: boolean) {
    if (value) {
      this.loadAmlDataForClient(this.client);
    }
  }

  inProgressAmlData$: Observable<AmlEntityType[] | null>;
  doneAmlData$: Observable<AmlEntityType[] | null>;
  hasActiveConnection$: Observable<boolean>;
  createAmlButtonText$: Observable<string>;
  tooltipOptions: TooltipOptions = { escape: false, tooltipStyleClass: 'ptl-tooltip' };
  dateFormatForDatePipe$: Observable<string>;

  private amlDataSubject = new BehaviorSubject<AmlEntityType[] | null>(null);

  constructor(
    private amlService: AmlService,
    private dialogService: DialogService,
  ) {
    super();
    this.inProgressAmlData$ = this.getInProgressObservable();
    this.doneAmlData$ = this.getDoneObservable();
    this.hasActiveConnection$ = this.store.select(FirmSelectors.selectHasActiveAmlConnection);
    this.createAmlButtonText$ = combineLatest([this.hasActiveConnection$, this.translationsLoaded$]).pipe(
      map(([hasActiveConnection, translations]) =>
        hasActiveConnection ? translations.createAmlCheck : translations.addRiskLevel,
      ),
    );
    this.dateFormatForDatePipe$ = this.translationsLoaded$.pipe(
      map(() => this.translate('shared.fullYearDateFormat', { useComponentScope: false })),
    );
  }

  createAml() {
    const createAmlWithDueAndOpenLink$ = this.amlService
      .createAmlForClient(this.client.id)
      .pipe(tap((response: AmlEntityType) => this.openLinkInNewTab(response.link)));

    this.hasActiveConnection$
      .pipe(
        take(1),
        mergeMap((hasActiveConnection) => {
          if (!hasActiveConnection) {
            return this.showCreateManualAmlDialog();
          }
          return createAmlWithDueAndOpenLink$;
        }),
      )
      .subscribe((response: AmlEntityType) => this.appendAmlData(response));
  }

  showAttachAmlDialog() {
    openTranslatableDialog({
      deps: { dialogService: this.dialogService, translationService: this.translationService, store: this.store },
      componentType: InputExistingAmlIdDialogComponent,
      data: null,
    }).subscribe((res) => this.attachExistingAml(res.amlId));
  }

  showCreateManualAmlDialog() {
    return openTranslatableDialog({
      deps: { dialogService: this.dialogService, translationService: this.translationService, store: this.store },
      componentType: CreateManualAmlDialogComponent,
      data: null,
    }).pipe(
      filter((response) => !!response),
      mergeMap((response) => this.amlService.createManualAmlForClient(this.client.id, response)),
    );
  }

  inProgressMenuItemsInContextOf(aml: AmlEntityType): MenuItemProps[] {
    return [
      {
        id: 'resumeAmlCheck',
        text: this.translate('resumeAmlCheck'),
        onAction: () => this.openLinkInNewTab(aml.link),
      },
    ];
  }

  doneMenuItemsInContextOf(amlEntity: AmlEntityType): MenuItemProps[] {
    if (!amlEntity.amlId) {
      return [
        {
          id: 'removeRiskAssessment',
          text: this.translate('removeRiskAssessment'),
          onAction: () => this.deleteAmlEntity(amlEntity),
        },
      ];
    }

    const openReportMenuItem: MenuItemProps = {
      id: 'downloadAmlReport',
      text: this.translate('downloadAmlReport'),
      onAction: () => this.openLinkInNewTab(amlEntity.documentLink),
    };

    return [
      {
        id: 'openAmlCheck',
        text: this.translate('openAmlCheck'),
        onAction: () => this.openLinkInNewTab(amlEntity.link),
      },
      ...(amlEntity.documentLink ? [openReportMenuItem] : []),
    ];
  }

  private attachExistingAml(amlId: string) {
    if (!amlId) {
      return;
    }
    this.amlService.attachExistingAmlForClient(this.client.id, amlId).subscribe((res) => this.appendAmlData(res));
  }

  private deleteAmlEntity(amlEntity: AmlEntityType) {
    this.amlService.deleteAmlEntity(amlEntity.id).subscribe(() => {
      const currentAmlData = this.amlDataSubject.getValue();
      this.amlDataSubject.next(currentAmlData.filter((amlData) => amlData.id !== amlEntity.id));
    });
  }

  private loadAmlDataForClient(client: Client) {
    this.amlDataSubject.next(null);

    this.amlService.loadAmlDataForClient(client.id).subscribe((res) => {
      this.amlDataSubject.next(res ?? []);
    });
  }

  private appendAmlData(amlData: AmlEntityType) {
    const currentAmlData = this.amlDataSubject.getValue();
    this.amlDataSubject.next([amlData, ...currentAmlData]);
  }

  private getInProgressObservable(): Observable<AmlEntityType[]> {
    const doneStatus = [AmlStatus.DONE, AmlStatus.DECLINED];
    return this.amlDataSubject
      .asObservable()
      .pipe(map((res) => res?.filter((amlData) => !doneStatus.includes(amlData.status))));
  }

  private getDoneObservable(): Observable<AmlEntityType[]> {
    const doneStatus = [AmlStatus.DONE, AmlStatus.DECLINED];
    return this.amlDataSubject
      .asObservable()
      .pipe(map((res) => res?.filter((amlData) => doneStatus.includes(amlData.status))));
  }

  private openLinkInNewTab(link: string | null) {
    if (!link) {
      return;
    }
    window.open(link, '_blank');
  }
}
